// client/src/components/secure/LoginPage.js
import React from 'react';
import { Link } from 'react-router-dom';
import {
    loadPasswordlessConfig, fetchTailnetTicket, tailnetSignIn, passkeysSupported, passkeySignIn,
    tailnetStartUrl, takeTailnetReturn, tailnetLoginsFor, tailnetSignInWith,
} from '../../utils/passwordless';

const TAILNET_ERRORS = {
    not_tailnet: 'This device did not come through the office Tailscale network. Switch Tailscale on and try again.',
    off: 'Tailscale sign-in is not set up on this site.',
    server: 'Tailscale sign-in hit a problem. Please use your password this time.',
};

// Password-free sign-in (routes/passwordless.js). On a device trusted on the office
// Tailscale network: one big button per login that trusts it — never pressed for the
// person. Anywhere (and in the BREC app): "Sign in with a passkey". Both quietly absent
// when switched off, off the tailnet, or unsupported.
const PasswordFreeOptions = ({ onSession }) => {
    const [cfg, setCfg] = React.useState(null);
    const [trusted, setTrusted] = React.useState(null);   // ticket answer with logins
    const [busy, setBusy] = React.useState(false);
    const [err, setErr] = React.useState('');
    const [returned, setReturned] = React.useState(null);  // { ticket, device, logins } from the page-visit door

    React.useEffect(() => {
        let live = true;
        (async () => {
            // Back from the tailnet host's page-visit door: one trusted login signs straight in
            // (the person pressed the button that started this); several → one button each.
            const back = takeTailnetReturn();
            if (back && back.error) setErr(TAILNET_ERRORS[back.error] || 'Tailscale sign-in did not work. Please use your password.');
            if (back && back.ticket) {
                try {
                    const r = await tailnetLoginsFor(back.ticket);
                    if (!live) return;
                    if (r.logins && r.logins.length === 1) {
                        const data = await tailnetSignInWith(back.ticket, r.logins[0].userId);
                        if (live && data && data.token) { onSession(data); return; }
                    } else if (r.logins && r.logins.length > 1) {
                        setReturned({ ticket: back.ticket, device: r.device, logins: r.logins });
                    } else {
                        setErr(`This device (${r.device}) is not trusted for any login yet. Sign in with your password, then use Password-free Sign-in → Trust this device.`);
                    }
                } catch (e) {
                    if (live) setErr(e.message || 'Tailscale sign-in did not work.');
                }
            }
            const c = await loadPasswordlessConfig();
            if (!live || !c) return;
            setCfg(c);
            if (c.tailnet && c.tailnet.signIn) {
                const t = await fetchTailnetTicket(c.tailnet.baseUrl, 2500);
                if (live && t && t.logins && t.logins.length) setTrusted(t);
            }
        })();
        return () => { live = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const go = async (fn) => {
        setBusy(true); setErr('');
        try {
            const data = await fn();
            if (data && data.token) onSession(data);
        } catch (e) {
            setErr(e.message || 'Sign-in failed.');
        } finally {
            setBusy(false);
        }
    };

    const showPasskey = !!(cfg && cfg.passkeys && cfg.passkeys.enabled && passkeysSupported());
    const startUrl = cfg && cfg.tailnet && cfg.tailnet.signIn ? tailnetStartUrl(cfg.tailnet.baseUrl) : null;
    if (!trusted && !returned && !startUrl && !showPasskey && !err) return null;
    return (
        <div className="space-y-3">
            {returned && returned.logins.map((l) => (
                <button key={`r${l.userId}`} type="button" disabled={busy}
                    onClick={() => go(() => tailnetSignInWith(returned.ticket, l.userId))}
                    className="w-full py-4 px-4 rounded-lg text-left text-white bg-emerald-700 hover:bg-emerald-800 disabled:opacity-60 shadow">
                    <span className="block text-lg font-semibold">Sign in as {l.name}</span>
                    <span className="block text-sm text-emerald-100">Trusted device: {returned.device} — no password needed</span>
                </button>
            ))}
            {trusted && trusted.logins.map((l) => (
                <button key={l.userId} type="button" disabled={busy}
                    onClick={() => go(() => tailnetSignIn(cfg.tailnet.baseUrl, l.userId))}
                    className="w-full py-4 px-4 rounded-lg text-left text-white bg-emerald-700 hover:bg-emerald-800 disabled:opacity-60 shadow">
                    <span className="block text-lg font-semibold">Sign in as {l.name}</span>
                    <span className="block text-sm text-emerald-100">Trusted device: {l.device} — no password needed</span>
                </button>
            ))}
            {/* Shown whenever the switch is on: the background check above is often blocked by
                Chrome, and only a device on the tailnet can open the start address anyway. */}
            {startUrl && !trusted && !returned && (
                <a href={startUrl}
                    className="block w-full py-4 px-4 rounded-lg text-left text-white bg-emerald-700 hover:bg-emerald-800 shadow">
                    <span className="block text-lg font-semibold">Sign in with this Tailscale device</span>
                    <span className="block text-sm text-emerald-100">No password — for office devices on our Tailscale network</span>
                </a>
            )}
            {showPasskey && (
                <button type="button" disabled={busy} onClick={() => go(passkeySignIn)}
                    className="w-full py-3 px-4 rounded-lg border-2 border-slate-800 text-slate-800 font-medium hover:bg-slate-50 disabled:opacity-60">
                    {busy ? 'Signing in…' : 'Sign in with a passkey (fingerprint, face or device PIN)'}
                </button>
            )}
            {err && <p className="text-sm text-center text-red-600">{err}</p>}
            <div className="flex items-center gap-3 text-xs text-gray-400">
                <span className="flex-1 border-t border-gray-200" />or use your password<span className="flex-1 border-t border-gray-200" />
            </div>
        </div>
    );
};

// Second sign-in step: the 6-digit code from the authenticator app, or one of
// the ten recovery codes.
const SecondFactorStep = ({ challenge, onVerify, onCancel, error, loading }) => {
    const [useRecovery, setUseRecovery] = React.useState(!challenge.authenticatorAvailable);
    const [value, setValue] = React.useState('');

    const submit = (e) => {
        e.preventDefault();
        onVerify(useRecovery ? { recoveryCode: value } : { code: value });
    };
    const flip = () => { setUseRecovery(!useRecovery); setValue(''); };

    return (
        <form className="mt-8 space-y-5" onSubmit={submit}>
            <div>
                <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-1">
                    {useRecovery ? 'Recovery code' : 'Authentication code'}
                </label>
                <input id="otp" name="otp" required autoFocus
                    type="text"
                    inputMode={useRecovery ? 'text' : 'numeric'}
                    autoComplete={useRecovery ? 'off' : 'one-time-code'}
                    pattern={useRecovery ? undefined : '[0-9 ]{6,7}'}
                    maxLength={useRecovery ? 20 : 7}
                    placeholder={useRecovery ? 'xxxxx-xxxxx' : '123456'}
                    className={`block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-slate-500 focus:border-slate-500 ${useRecovery ? 'font-mono' : 'text-center text-2xl tracking-[0.4em] font-mono'}`}
                    value={value} onChange={(e) => setValue(e.target.value)} />
                <p className="mt-2 text-xs text-gray-500">
                    {useRecovery
                        ? 'Each recovery code works once. Enter one from the list you saved when you set up two-factor.'
                        : 'Open your authenticator app (Google Authenticator, Microsoft Authenticator, Authy, 1Password…) and enter the 6-digit code shown for this portal.'}
                </p>
                {!challenge.authenticatorAvailable && (
                    <p className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                        Authenticator codes can't be checked right now because of a server setting. Use a recovery code, or contact the managing broker.
                    </p>
                )}
            </div>
            {error && <p className="text-sm text-center text-red-600">{error}</p>}
            <button type="submit" disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-slate-800 hover:bg-slate-900 disabled:bg-indigo-400">
                {loading ? 'Checking…' : 'Verify'}
            </button>
            <div className="flex justify-between text-sm">
                <button type="button" onClick={flip} className="text-indigo-600 hover:underline">
                    {useRecovery ? 'Use the authenticator app instead' : 'Use a recovery code'}
                </button>
                <button type="button" onClick={onCancel} className="text-gray-500 hover:underline">Start over</button>
            </div>
            <p className="text-xs text-gray-500 text-center">
                Lost your phone and your recovery codes? Contact the managing broker to reset your two-factor.
            </p>
        </form>
    );
};

const LoginPage = ({ onLogin, error, loading, challenge, onVerify, onCancelVerify, onSession }) => {
    const [username, setUsername] = React.useState('');
    const [password, setPassword] = React.useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        onLogin(username, password);
    };

    if (challenge) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="w-full max-w-md p-8 bg-white rounded-xl shadow-lg">
                    <div className="text-center">
                        <h2 className="text-2xl font-bold text-gray-900">Two-factor verification</h2>
                        <p className="mt-2 text-sm text-gray-600">Your password was accepted. One more step.</p>
                    </div>
                    <SecondFactorStep challenge={challenge} onVerify={onVerify} onCancel={onCancelVerify}
                        error={error} loading={loading} />
                </div>
            </div>
        );
    }

    return (
        <div className="flex items-center justify-center min-h-screen">
            <div className="w-full max-w-md p-8 space-y-8 bg-white rounded-xl shadow-lg">
                <div className="text-center">
                    <Link to="/" className="text-sm text-indigo-600 hover:underline">&larr; Back to Home</Link>
                    <h2 className="text-3xl font-bold text-gray-900 mt-4">Secure Portal Login</h2>
                    <p className="mt-2 text-sm text-gray-600">Welcome to Business Real Estate Corp.</p>
                </div>
                {onSession && <PasswordFreeOptions onSession={onSession} />}
                <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                    <div className="rounded-md shadow-sm -space-y-px">
                        <div>
                            <input id="username" name="username" type="text" autoComplete="username" required
                                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-slate-500 focus:border-slate-500 focus:z-10 sm:text-sm"
                                placeholder="Username" value={username} onChange={(e) => setUsername(e.target.value)} />
                        </div>
                        <div>
                            <input id="password" name="password" type="password" autoComplete="current-password" required
                                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-slate-500 focus:border-slate-500 focus:z-10 sm:text-sm"
                                placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
                        </div>
                    </div>
                    {error && <p className="text-sm text-center text-red-600">{error}</p>}
                    <div className="text-right">
                        <Link to="/forgot-password" className="text-sm text-indigo-600 hover:underline">Forgot password?</Link>
                    </div>
                    <div>
                        <button type="submit" disabled={loading}
                            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-slate-800 hover:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 disabled:bg-indigo-400">
                            {loading ? 'Signing in...' : 'Sign in'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default LoginPage;
